/**
 * EidDecoration — compact festive header top-bar for Eid Al Fitr.
 *
 * Usage:
 *   <EidDecoration />
 *
 * Mobile: text + minimal emoji only (no balloons — too crowded).
 * Desktop (sm+): adds animated mini-balloon decorations on both sides.
 */

import { cn } from "@design-system/lib/utils";

// ── MiniBalloon ───────────────────────────────────────────────────────────────

function MiniBalloon({ color, className }: { color: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 20 30"
      className={cn("flex-shrink-0", className)}
      aria-hidden="true"
    >
      <ellipse cx="10" cy="10" rx="7" ry="9" fill={color} opacity="0.9" />
      <ellipse cx="7" cy="6" rx="2" ry="2.5" fill="white" opacity="0.3" />
      <ellipse cx="10" cy="19" rx="1.5" ry="1" fill={color} opacity="0.8" />
      <path d="M10 20 Q9 24 10 27" stroke="#94a3b8" strokeWidth="0.8" fill="none" />
    </svg>
  );
}

// ── ConfettiPill ─────────────────────────────────────────────────────────────

function ConfettiPill({ className }: { className?: string }) {
  return <div className={cn("h-1.5 w-3 flex-shrink-0 rounded-full", className)} />;
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function EidDecoration() {
  return (
    <div className="relative w-full overflow-hidden" dir="rtl">
      {/* Rainbow ribbon strip at very top */}
      <div className="flex h-1 w-full overflow-hidden">
        {["bg-rose-400", "bg-amber-400", "bg-emerald-400", "bg-teal-400", "bg-sky-400", "bg-purple-400", "bg-pink-400", "bg-lime-400"].map(
          (c, i) => <div key={i} className={cn("flex-1", c)} />,
        )}
      </div>

      {/* Main bar */}
      <div className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 px-3 py-1 sm:gap-3 sm:px-4">

        {/* Left side — hidden on mobile, visible sm+ */}
        <div className="hidden items-center gap-1 sm:flex">
          <ConfettiPill className="rotate-12 bg-amber-300 opacity-80" />
          <MiniBalloon color="#f87171" className="h-5 w-3 animate-[sway_3s_ease-in-out_infinite]" />
          <MiniBalloon color="#fbbf24" className="h-6 w-3.5 animate-[sway_3.5s_ease-in-out_infinite_0.5s]" />
          <ConfettiPill className="-rotate-6 bg-purple-300 opacity-80" />
        </div>

        {/* Mobile: single balloon emoji each side */}
        <span className="sm:hidden" aria-hidden="true">🎈</span>

        {/* Center text — always visible */}
        <span
          className={cn(
            "min-w-0 flex-1 text-center sm:flex-none",
            "animate-shimmer bg-[length:200%_auto] bg-clip-text text-transparent",
            "bg-gradient-to-r from-amber-200 via-white to-amber-200",
            // Slightly smaller on mobile so text never wraps
            "text-[10px] font-bold tracking-wide sm:text-sm sm:tracking-widest",
          )}
          style={{ fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif" }}
        >
          🎉 عيد الفطر المبارك 🎊
        </span>

        {/* Mobile: single balloon emoji */}
        <span className="sm:hidden" aria-hidden="true">🎈</span>

        {/* Right side — hidden on mobile */}
        <div className="hidden items-center gap-1 sm:flex">
          <ConfettiPill className="rotate-6 bg-rose-300 opacity-80" />
          <MiniBalloon color="#34d399" className="h-6 w-3.5 animate-[sway_3.2s_ease-in-out_infinite_0.3s]" />
          <MiniBalloon color="#a78bfa" className="h-5 w-3 animate-[sway_2.8s_ease-in-out_infinite_0.7s]" />
          <ConfettiPill className="-rotate-12 bg-teal-300 opacity-80" />
        </div>
      </div>
    </div>
  );
}
